<script lang="ts" setup>
import type { ComponentPublicInstance } from 'vue';
import { ref, watch, onMounted, reactive } from 'vue';

import AppTab from './AppTab.vue';
import type { AppTabsProps } from './AppTabs.model';

const props = defineProps<AppTabsProps>();

interface AppTabsEmits {
  (eventName: 'tab-click', key: string): void;
}

const emit = defineEmits<AppTabsEmits>();

const wrapperRef = ref<HTMLElement | null>(null);
const tabRefs = reactive<Array<Element | ComponentPublicInstance | null>>([]);
const indicatorStyle = ref<Record<string, string>>({});

function updateIndicatorPosition(): void {
  const index = props.tabs.findIndex((tab) => tab.key === props.selectedTabKey);
  const tabRef = tabRefs[index];
  const wrapper = wrapperRef.value;
  if (!tabRef || !wrapper) {
    return;
  }

  const tabEl = (tabRef as ComponentPublicInstance).$el as HTMLElement;
  if (!tabEl) {
    return;
  }
  const left = tabEl.offsetLeft;
  const width = tabEl.offsetWidth;
  const wrapperWidth = wrapper.clientWidth;

  if (props.type === 'wide') {
    const tabCenter = left + width / 2;
    const indicatorPosition = tabCenter - wrapperWidth / 2;

    indicatorStyle.value = {
      transform: `translateX(${indicatorPosition}px)`,
      width: `${width}px`,
    };
    wrapper.scrollTo({
      left: indicatorPosition,
      behavior: 'smooth',
    });
  }

  if (props.type === 'compact') {
    let indicatorPosition = 0;
    props.tabs.forEach((_, tabIndex) => {
      if (tabIndex < index) {
        const tabRef = tabRefs[tabIndex];
        const tabEl = (tabRef as ComponentPublicInstance).$el as HTMLElement;
        if (!tabEl) {
          return;
        }

        indicatorPosition += tabEl.offsetWidth;
      }
    });
    indicatorStyle.value = {
      transform: `translateX(${indicatorPosition}px)`,
      width: `${width}px`,
    };
    wrapper.scrollTo({
      left: indicatorPosition,
      behavior: 'smooth',
    });
  }
}

watch(
  () => props.selectedTabKey,
  () => {
    updateIndicatorPosition();
  },
  { immediate: true }
);

onMounted(() => {
  updateIndicatorPosition();
});
</script>

<template>
  <div ref="wrapperRef" class="tabs-wrapper" :class="{ [props.type]: true }">
    <AppTab
      v-for="(tab, index) in tabs"
      :ref="
        (el) => {
          tabRefs[index] = el;
        }
      "
      :key="tab.key"
      class="tab"
      :title="tab.title"
      :icon="tab.icon"
      :is-open="tab.key === selectedTabKey"
      :type="props.type"
      @click="emit('tab-click', tab.key)"
    />
    <div class="selected-tab-indicator" :style="indicatorStyle" />
  </div>
</template>

<style lang="scss" scoped>
.tabs-wrapper {
  position: relative;
  display: flex;
  overflow-x: auto;
  align-items: flex-start;
  white-space: nowrap;
  scrollbar-width: none;

  .selected-tab-indicator {
    position: absolute;
    bottom: 0;
    height: 3px;
    transition: transform var(--transition-medium),
      width var(--transition-medium);
    will-change: transform, width;
  }

  &.wide {
    justify-content: space-around;
    width: 100%;

    .tab {
      flex: 1;
      border-bottom: 1px solid var(--color-neutral-50);
    }

    .selected-tab-indicator {
      background-color: var(--color-neutral-70);
    }
  }

  &.compact {
    justify-content: flex-start;

    .selected-tab-indicator {
      background-color: var(--color-brand-violet);
    }
  }
}
</style>
